import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { invalidateClaimsQueries } from '@/admin/hooks/use-claims'

/**
 * Claim Team (WPS-160) - the per-slot assignees of a claim: the primary owner
 * plus the named slots (backup RS, lab, settlement, inspector). The slot
 * catalogue is tenant config, so the surface is entirely data-driven and only
 * appears for tenants that have one (the query is gated by the caller on the
 * `advanced_routing` capability; the backend 404s otherwise).
 */

export interface TeamAssignee {
  uuid: string
  name: string
}

export interface TeamSlot {
  key: string
  label: string
  role: string
  primary: boolean
  /**
   * The workflow-state `role_label` this slot owns, or null. When the claim's
   * current state carries this same `role_label`, the claim header shows this
   * slot's assignee as the active-stage owner (WPS-160). Config-vs-config, so
   * the frontend carries no tenant vocabulary.
   */
  stage_role_label: string | null
  assignee: TeamAssignee | null
  /** Users who may hold this slot (active members holding the slot's role). */
  assignable: TeamAssignee[]
}

export interface ClaimTeam {
  slots: TeamSlot[]
}

export function useClaimTeam(claimUuid: string, enabled: boolean) {
  return useQuery<ClaimTeam>({
    queryKey: ['claim-team', claimUuid],
    enabled: !!claimUuid && enabled,
    queryFn: () =>
      api
        .get(`/claims/claims/${claimUuid}/team/`)
        .then((r) => r.data?.result ?? r.data),
  })
}

export function useSetTeamSlot(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation<
    TeamSlot,
    Error,
    { slotKey: string; userUuid: string | null }
  >({
    mutationFn: ({ slotKey, userUuid }) =>
      api
        .post(`/claims/claims/${claimUuid}/team/${slotKey}/`, {
          user_uuid: userUuid,
        })
        .then((r) => r.data?.result ?? r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claim-team', claimUuid] })
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
      invalidateClaimsQueries(qc)
    },
  })
}
